import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Shuffle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorMessage } from "@/lib/api";

export function DistributeButton({ unassigned = 0 }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: () => api.get("/team").then((r) => r.data) });
  const execs = team.filter((m) => m.role === "executive" && !m.on_leave);
  const run = useMutation({
    mutationFn: () => api.post("/tickets/distribute").then((r) => r.data),
    onSuccess: (d) => {
      setOpen(false);
      if (!d.distributed) return toast.info(d.message || "Nothing to distribute");
      toast.success(`${d.distributed} lead${d.distributed === 1 ? "" : "s"} distributed · ${d.per_user.map((p) => `${p.name} +${p.assigned}`).join(", ")}`);
      ["tickets", "stats", "admin-overview", "my-performance"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <>
      <button type="button" className="btn-dark !py-2 !text-sm" onClick={() => setOpen(true)} disabled={!unassigned} data-testid="distribute-button">
        <Shuffle className="h-4 w-4" /> Distribute {unassigned || ""} unassigned
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="border-2 border-black" data-testid="distribute-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Distribute {unassigned} unassigned lead{unassigned === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Leads are shared evenly across {execs.length} active executive{execs.length === 1 ? "" : "s"} ({execs.map((e) => e.name).join(", ") || "none available"}), balancing each person's current open load. High-priority leads go first. Executives can still pick up leads themselves anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="distribute-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-black hover:bg-zinc-800" disabled={!execs.length || run.isPending} onClick={() => run.mutate()} data-testid="distribute-confirm-button">
              {run.isPending ? "Distributing…" : "Distribute now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
